<script lang="ts" setup>
import { format, isSameDay, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'
import { ToccoModuleRunFieldsFragment } from '~/api/graphql/generated'

type ReservationDay = { date: string; lecturers: string[]; startTime?: string; endTime?: string }

const props = defineProps<{
  run?: ToccoModuleRunFieldsFragment
  name: string
  selected: boolean
  eLearning?: boolean
}>()

const emit = defineEmits<{
  select: []
}>()

const { t } = useI18n()

const formatDate = (value?: string | null) => (value ? format(parseISO(value), 'dd.MM.yyyy') : '')

const startDate = computed(() => formatDate(props.run?.fieldStartDate?.value))
const endDate = computed(() => formatDate(props.run?.fieldEndDate?.value))
const showEndDate = computed(() => {
  const startValue = props.run?.fieldStartDate?.value
  const endValue = props.run?.fieldEndDate?.value
  return Boolean(endValue && (!startValue || !isSameDay(parseISO(startValue), parseISO(endValue))))
})

const badge = computed(() => {
  if (props.run?.fieldParticipationMax === null || props.run?.fieldParticipationMax === undefined) return null
  const freePlaces = props.run.fieldParticipationMax - (props.run.fieldRegistrationCount ?? 0)
  if (freePlaces <= 0) return { key: 'waitingList', label: t('tocco.runSelection.badges.waitingList') }
  if (freePlaces <= 3) return { key: 'lastPlaces', label: t('tocco.runSelection.badges.lastPlaces') }
  return null
})

const parseReservationDays = (raw?: string | null): ReservationDay[] => {
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

const reservationDays = computed<ReservationDay[]>(() => {
  const days = parseReservationDays(props.run?.fieldReservationDays)
  if (days.length) return days
  return [
    ...new Set(
      (props.run?.fieldReservationDates ?? []).map((date) => date?.value).filter((value): value is string => !!value),
    ),
  ]
    .sort()
    .map((date) => ({ date, lecturers: [] }))
})

// CARW-1702: the importer emits optional Europe/Zurich-local HH:mm day bounds.
// Append them only when both are real, ordered clock times — never guess.
const parseClockTime = (value?: string): number | null => {
  if (!value || !/^\d{2}:\d{2}$/.test(value)) return null
  const hours = Number(value.slice(0, 2))
  const minutes = Number(value.slice(3, 5))
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

const teachingDays = computed(() =>
  reservationDays.value.map((day) => {
    const date = format(parseISO(day.date), 'EEEE, dd. LLLL yyyy', { locale: de })
    const start = parseClockTime(day.startTime)
    const end = parseClockTime(day.endTime)
    const withTimes = start !== null && end !== null && start < end
    return {
      date: withTimes ? `${date}, ${day.startTime} - ${day.endTime}` : date,
      lecturers: (day.lecturers ?? []).join(', '),
    }
  }),
)

const isExpanded = ref(false)
</script>

<template>
  <div class="run-row">
    <div class="run-row-main">
      <label class="run-select">
        <input class="radio" type="radio" :name="name" :checked="selected" @change="emit('select')" />
        <span v-if="eLearning" class="dates">
          <span class="date text-small">{{ t('tocco.runSelection.eLearning') }}</span>
        </span>
        <span v-else class="dates">
          <span class="date text-small">{{ startDate }}</span>
          <Icon v-if="showEndDate" name="mini-arrow-regular" />
          <span v-if="showEndDate" class="date text-small">{{ endDate }}</span>
        </span>
        <span v-if="eLearning" class="city text-small">{{ t('tocco.runSelection.online') }}</span>
        <span v-else-if="run?.fieldCity" class="city text-small">{{ run.fieldCity }}</span>
      </label>

      <div v-if="!eLearning" class="run-meta">
        <span v-if="badge" :class="['badge', 'text-xsmall', 'f-medium', badge.key]">{{ badge.label }}</span>
        <button
          v-if="teachingDays.length"
          class="teaching-days-toggle"
          type="button"
          :aria-expanded="isExpanded"
          :aria-controls="`teaching-days-${run?.id}`"
          @click="isExpanded = !isExpanded"
        >
          <span class="teaching-days-count">{{ teachingDays.length }}</span>
          {{ t('tocco.runSelection.teachingDays') }}
          <Icon :name="isExpanded ? 'mini-minus-small' : 'mini-plus-small'" />
        </button>
      </div>
    </div>

    <div v-if="!eLearning && isExpanded && teachingDays.length" :id="`teaching-days-${run?.id}`" class="teaching-days">
      <ul>
        <li v-for="(day, index) in teachingDays" :key="index" class="teaching-day">
          <span class="day-date text-small">{{ day.date }}</span>
          <span v-if="day.lecturers" class="day-lecturers text-small">{{ day.lecturers }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.run-row {
  border-bottom: 1px solid var(--c-line-light);
  padding: 24px 0;

  &:last-child {
    border-bottom: 0;
  }
}

.run-row-main {
  display: flex;
  flex-direction: column;
  gap: 16px;

  // One-line row only from ds up: below that the venue string (full
  // address in fieldCity) has no room and collapses to per-character
  // wrapping next to the badge and teaching-days control.
  @include breakpoint(ds) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
}

.run-select {
  display: grid;
  grid-template-columns: 26px minmax(0, 1fr);
  align-items: start;
  gap: 8px 16px;
  flex: 1;
  min-width: 0;

  cursor: pointer;

  @include breakpoint(ds) {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .radio {
    appearance: none;

    position: relative;
    grid-row: 1 / span 2;

    width: 26px;
    height: 26px;
    min-width: 26px;

    margin: 0;

    border: 1px solid var(--form-input-border-color);
    border-radius: 50%;

    background: var(--c-white);

    cursor: pointer;

    @include transitions(border, background);

    &:checked {
      background: var(--c-blue-100);
      border-color: var(--c-blue-100);

      &:after {
        content: '';

        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        width: 13px;
        height: 13px;

        border-radius: 50%;
        background: var(--c-white);
      }
    }
  }

  .dates {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px 8px;
    min-width: 0;

    color: var(--c-primary);
    font-weight: bold;

    // MarkOT has no tabular figures, so date strings render at slightly different
    // widths per row; without a shared floor the city column shifts per row and
    // wraps inconsistently.
    @include breakpoint(ds) {
      flex-wrap: nowrap;
      flex-shrink: 0;
      gap: 12px;
      min-width: 13em;
    }

    :deep(svg) {
      flex-shrink: 0;
      width: 14px;
      height: auto;
    }
  }

  .city {
    min-width: 0;
    overflow-wrap: anywhere;

    color: var(--c-gray-100);
  }
}

.run-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  min-width: 0;

  @include breakpoint(ds) {
    justify-content: flex-end;
  }
}

.badge {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid currentColor;

  white-space: nowrap;

  &.lastPlaces {
    color: var(--c-green-dark-100);
  }

  &.waitingList {
    color: var(--c-red-dark-100);
  }
}

.teaching-days-toggle {
  display: flex;
  align-items: center;
  gap: 6px;

  padding: 12px 12px 12px 18px;

  border: 1px solid rgba(var(--c-blue-100-rgb), 0.6);
  background: none;

  color: var(--c-primary);
  font-weight: bold;
  text-transform: uppercase;

  @include f-13;

  cursor: pointer;

  @include transitions(border-color);

  @include breakpoint(tl) {
    gap: 8px;
    padding: 16px 20px 16px 24px;
  }

  @include hover {
    border-color: var(--c-blue-rollover);
  }

  :deep(svg) {
    width: 14px;
    height: auto;
  }
}

.teaching-days-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 28px;

  width: 28px;
  height: 28px;

  border-radius: 50%;
  background: var(--c-blue-light-100);

  color: var(--c-primary);
}

.teaching-days {
  padding: 16px 0 0;

  // Tablet/desktop keep the pre-existing indent (42px radio column + the
  // 40px UA list padding, now explicit); mobile runs full width (CARW-1693).
  @include breakpoint(t) {
    padding-left: 82px;
  }

  ul {
    display: flex;
    flex-direction: column;
    gap: 8px;

    padding-left: 0;
  }
}

.teaching-day {
  display: flex;
  flex-direction: column;
  gap: 2px;

  padding: 12px 16px;

  border: 1px solid var(--c-line-light);
  border-radius: 8px;

  .day-date {
    color: var(--c-primary);
    font-weight: bold;
  }

  .day-lecturers {
    color: var(--c-gray-100);
  }
}
</style>
